import {
    BookingFlowPatientPrefillInfo,
    BookingFlowVisitPrefillInfo,
} from '@/pages/BookingFlow/BookingFlow';
import { sexToCodeGql } from '@/types/sex';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import moment from 'moment-timezone';
import { useMemo } from 'react';

import { useGetSearchParam } from '../router';
import {
    DecryptPrefillDataForBookingFlowQuery,
    DecryptPrefillDataForBookingFlowQueryVariables,
} from './usePrefillData.gql.types';

const DECRYPT_PREFILL_DATA = gql`
    query DecryptPrefillDataForBookingFlow($encryptedData: String!) {
        decryptPrefillData(encryptedData: $encryptedData) {
            patientPrefillData {
                firstName
                lastName
                dateOfBirth
                email
                phone
                sex
                preferredLanguage
                address {
                    line1
                    line2
                    city
                    state
                    zipCode
                }
                metadata
                externalId
            }
            visitPrefillData {
                metadata
                notesForClinician
                serviceBundle {
                    id
                }
                serviceArea {
                    id
                }
            }
        }
    }
`;

const toPatientPrefillInfo = (
    data: DecryptPrefillDataForBookingFlowQuery['decryptPrefillData']['patientPrefillData'],
): BookingFlowPatientPrefillInfo => {
    return {
        first_name: data.firstName ?? undefined,
        last_name: data.lastName ?? undefined,
        email: data.email ?? undefined,
        phone: data.phone ?? undefined,
        dob: data.dateOfBirth ? moment(data.dateOfBirth) : undefined,
        sex: data.sex ? sexToCodeGql(data.sex) : undefined,
        address1: data.address?.line1 ?? undefined,
        address2: data.address?.line2 ?? undefined,
        city: data.address?.city ?? undefined,
        state: data.address?.state ?? undefined,
        zip_code: data.address?.zipCode ?? undefined,
        patient_meta: data.metadata ? JSON.stringify(data.metadata) : undefined,
        client_patient_id: data.externalId ?? undefined,
    };
};

const toVisitPrefillInfo = (
    data: DecryptPrefillDataForBookingFlowQuery['decryptPrefillData']['visitPrefillData'],
): BookingFlowVisitPrefillInfo => {
    return {
        metadata: data.metadata ?? undefined,
        notes_for_clinician: data.notesForClinician ?? undefined,
        service_area_id: data.serviceArea?.id ?? undefined,
        service_bundle_id: data.serviceBundle?.id ?? undefined,
    };
};

export const usePrefillData = (): {
    patientPrefillInfo: BookingFlowPatientPrefillInfo | null;
    visitPrefillInfo: BookingFlowVisitPrefillInfo | null;
    xData: string | null;
    loading: boolean;
} => {
    const [xData] = useGetSearchParam('xdata');
    const hasXData = !!xData;
    const encryptedData = xData ?? '';

    const { data: decryptPrefillDataData, loading } = useQuery<
        DecryptPrefillDataForBookingFlowQuery,
        DecryptPrefillDataForBookingFlowQueryVariables
    >(DECRYPT_PREFILL_DATA, {
        variables: {
            encryptedData,
        },
        skip: !hasXData,
    });

    const patientPrefillInfo = useMemo(
        () =>
            decryptPrefillDataData?.decryptPrefillData.patientPrefillData
                ? toPatientPrefillInfo(decryptPrefillDataData.decryptPrefillData.patientPrefillData)
                : null,
        [decryptPrefillDataData],
    );

    const visitPrefillInfo = useMemo(
        () =>
            decryptPrefillDataData?.decryptPrefillData.visitPrefillData
                ? toVisitPrefillInfo(decryptPrefillDataData.decryptPrefillData.visitPrefillData)
                : null,
        [decryptPrefillDataData],
    );

    return {
        patientPrefillInfo,
        visitPrefillInfo,
        xData,
        loading,
    };
};
